import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import liff from '@line/liff';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { ApiLineLiffService } from './api/Line-liff/api-line-liff';
import { ApiUserService } from './api/User/api-user';
import { LineAuthSessionResponse, UpdateProfilePicturePayload } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class LiffService {
  private lastIdToken: string | null = null;

  constructor(
    private cookie: CookieService,
    private apiLineLiffService: ApiLineLiffService,
    private apiUserService: ApiUserService,
  ) { }

  /** Mock LIFF only when useMockLiff (local Angular). Deployed builds must be false. */
  private get isTestMode(): boolean {
    return !!environment.useMockLiff;
  }

  private get mockUserId(): string {
    return environment.mockLineUid || '';
  }

  private get mockDisplayName(): string {
    return environment.mockDisplayName || 'LIFF Test';
  }

  private get mockPictureUrl(): string {
    return (
      environment.mockPictureUrl ||
      'https://i.pinimg.com/1200x/e5/b6/02/e5b602f54236b1cca870e3f88c326268.jpg'
    );
  }

  private get mockLanguage(): 'TH' | 'EN' {
    return (environment.mockLanguage as string) === 'EN' ? 'EN' : 'TH';
  }

  async initLiff(): Promise<void> {
    if (this.isTestMode) {
      if (!this.mockUserId) {
        throw new Error('environment.mockLineUid is required when useMockLiff=true');
      }
      this.setIdentity(
        this.mockUserId,
        this.mockDisplayName,
        this.mockPictureUrl,
        this.mockLanguage,
      );
      this.lastIdToken = null;
      console.log('[LIFF] mock mode (useMockLiff=true)', {
        liffId: environment.publicLiffId,
        userId: this.mockUserId,
        displayName: this.mockDisplayName,
        pictureUrl: this.mockPictureUrl,
        language: this.mockLanguage,
        auth: 'devLineUid → API (AllowDevUidBypass)',
      });
      return;
    }

    try {
      await liff.init({ liffId: environment.publicLiffId });

      if (!liff.isLoggedIn()) {
        console.warn('[LIFF] not logged in → redirect liff.login()', {
          liffId: environment.publicLiffId,
          isInClient: liff.isInClient(),
        });
        liff.login();
        return;
      }

      const profile = await liff.getProfile();
      const lang = liff.getLanguage();
      let lang_set = 'TH';
      if (lang == 'th-TH' || lang == 'th' || lang == 'TH') lang_set = 'TH';
      else lang_set = 'EN';

      this.lastIdToken = liff.getIDToken() || null;

      console.log('[LIFF] getProfile + getIDToken success', {
        liffId: environment.publicLiffId,
        isInClient: liff.isInClient(),
        isLoggedIn: liff.isLoggedIn(),
        language: lang,
        languageSet: lang_set,
        userId: profile.userId,
        displayName: profile.displayName,
        pictureUrl: profile.pictureUrl || '',
        statusMessage: profile.statusMessage ?? null,
        idTokenPresent: !!this.lastIdToken,
        idTokenLength: this.lastIdToken?.length ?? 0,
        idTokenPreview: this.lastIdToken
          ? `${this.lastIdToken.slice(0, 24)}…${this.lastIdToken.slice(-12)}`
          : null,
        idToken: this.lastIdToken,
      });

      this.setIdentity(
        profile.userId,
        profile.displayName,
        profile.pictureUrl || '',
        lang_set,
      );
    } catch (err) {
      console.error('LIFF init failed:', err);
      throw err;
    }
  }

  private setIdentity(
    userId: string,
    displayName: string,
    pictureUrl: string,
    language: string,
  ): void {
    this.cookie.set('line_uid', userId);
    this.cookie.set('display_name', displayName);
    this.cookie.set('picture_url', pictureUrl);
    this.cookie.set('language', language);
    this.cookie.set('picture_url', pictureUrl);

    localStorage.setItem('line_uid', userId);
    localStorage.setItem('display_name', displayName);
    localStorage.setItem('picture_url', pictureUrl);
    localStorage.setItem('language', language);
    localStorage.setItem('picture_url', pictureUrl);
  }

  private buildAuthBody(): {
    idToken?: string;
    devLineUid?: string;
    displayName?: string;
  } {
    const body: {
      idToken?: string;
      devLineUid?: string;
      displayName?: string;
    } = {
      displayName:
        this.cookie.get('display_name') ||
        localStorage.getItem('display_name') ||
        undefined,
    };

    if (this.isTestMode) {
      body.devLineUid = this.getUserId() || this.mockUserId;
    } else {
      const idToken = this.lastIdToken || liff.getIDToken();
      if (idToken) {
        body.idToken = idToken;
      }
    }

    return body;
  }

  private applyAuthResponse(res: LineAuthSessionResponse): void {
    if (!res?.success) return;

    if (res.uid) {
      this.cookie.set('line_uid', res.uid);
      localStorage.setItem('line_uid', res.uid);
    }
    if (res.displayName) {
      this.cookie.set('display_name', res.displayName);
      localStorage.setItem('display_name', res.displayName);
    }
    if (res.pictureUrl) {
      this.cookie.set('picture_url', res.pictureUrl);
      localStorage.setItem('picture_url', res.pictureUrl);
    } else {
      this.cookie.set('picture_url', this.mockPictureUrl);
      localStorage.setItem('picture_url', this.mockPictureUrl);
    }

    if (res.registered) {
      this.cookie.set('login_status', 'logged_in');
      localStorage.setItem('login_status', 'logged_in');
    } else {
      this.cookie.delete('login_status');
      localStorage.removeItem('login_status');
    }

    this.cookie.delete('token_user');
    localStorage.removeItem('token_user');
  }

  /**
   * After LIFF profile: lookup uid → member token OR reg_pending session (insert).
   */
  async completeAuthAfterLiff(): Promise<LineAuthSessionResponse | null> {
    try {
      const body = this.buildAuthBody();
      console.log('[LIFF] auth body → API', {
        hasIdToken: !!body.idToken,
        idTokenLength: body.idToken?.length ?? 0,
        idTokenPreview: body.idToken
          ? `${body.idToken.slice(0, 24)}…${body.idToken.slice(-12)}`
          : null,
        idToken: body.idToken ?? null,
        hasDevLineUid: !!body.devLineUid,
        devLineUid: body.devLineUid ?? null,
        displayName: body.displayName ?? null,
      });

      if (!body.idToken && !body.devLineUid) {
        console.warn('Missing LINE credentials');
        return null;
      }

      const lookup = await firstValueFrom(
        this.apiLineLiffService.lookupAuth(body),
      );
      console.log('[LIFF] lookup response', lookup);

      if (!lookup?.success) {
        console.error('lookup failed', lookup);
        return null;
      }

      let res: LineAuthSessionResponse;

      if (lookup.registered) {
        // uid มีและ active → ข้าม session ไปขอ member token
        res = await firstValueFrom(
          this.apiLineLiffService.issueMemberToken(body),
        );
      } else {
        // ไม่มี uid หรือ pending → session สำหรับ insert / OTP
        res = await firstValueFrom(
          this.apiLineLiffService.establishSession(body),
        );
      }
      console.log("res => ", res)

      this.applyAuthResponse(res);
      return res;
    } catch (err: any) {
      console.error('completeAuthAfterLiff error:', err);
      console.error('[LIFF] lookup/token/session HTTP error body', {
        status: err?.status,
        message: err?.error?.message,
        messageEn: err?.error?.messageEn,
        error: err?.error,
      });
      return null;
    }
  }

  /** @deprecated Use completeAuthAfterLiff() */
  async establishSession(): Promise<LineAuthSessionResponse | null> {
    return this.completeAuthAfterLiff();
  }

  /** @deprecated Use completeAuthAfterLiff() */
  async checkRegister(): Promise<any> {
    const session = await this.completeAuthAfterLiff();
    if (!session?.success) return null;
    return {
      code: session.registered ? '000' : '001',
      registered: session.registered,
      token: null,
    };
  }

  async updateName(name: string) {
    const uid = this.getUserId();
    if (!uid) {
      console.warn('Missing UID');
      return null;
    }

    const pictureUrl = localStorage.getItem('picture_url') || this.cookie.get('picture_url');
    if (!pictureUrl) {
      console.warn('Missing pictureUrl');
      return null;
    }

    try {
      return await firstValueFrom(this.apiUserService.updateNameApi(uid, name, pictureUrl));
    } catch (err) {
      console.error('updateName error:', err);
      return null;
    }
  }

  /*#################################################### Function ################################################*/
  /**
   * อัปเดตรูปภาพโปรไฟล์ผู้ใช้ใน Cookie, LocalStorage และยิง API อัปเดตไปยังระบบหลังบ้าน/LINE
   */
  async updateProfilePicture(pictureUrl: string): Promise<boolean> {
    const uid = this.getUserId();
    this.cookie.set('picture_url', pictureUrl);
    localStorage.setItem('picture_url', pictureUrl);

    if (!uid) return true;

    try {
      const payload: UpdateProfilePicturePayload = { uid, pictureUrl };
      await firstValueFrom(this.apiUserService.updateProfilePicture(payload));
      return true;
    } catch (err) {
      console.error('updateProfilePicture error:', err);
      return false;
    }
  }

  isLoggedIn(): boolean {
    try {
      return liff.isLoggedIn();
    } catch {
      return !!this.getUserId();
    }
  }

  getUserId(): string {
    return (
      this.cookie.get('line_uid') || localStorage.getItem('line_uid') || ''
    );
  }

  logout(): void {
    try {
      liff.logout();
    } catch {
      /* ignore outside LIFF */
    }
    this.cookie.deleteAll();
    localStorage.clear();
    window.location.reload();
  }

  /**
   * liff.init runs only on liff-loader / register — private pages reopened directly (reload, restored WebView)
   * skip it, and APIs like scanCodeV2 report unavailable until init completes.
   */
  async ensureReady(): Promise<boolean> {
    if (this.isTestMode) return false;
    try {
      if (liff.id) {
        await liff.ready;
      } else {
        await liff.init({ liffId: environment.publicLiffId });
      }
      return true;
    } catch (err) {
      console.warn('[LIFF] ensureReady failed', err);
      return false;
    }
  }
}
